import React, { useState, useCallback, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/root.css";
import { findPostBySlug } from "../data/posts";
import { useLocale } from "../context/LocaleContext";

function NewsDetail() {
  const { slug } = useParams();
  const { lang, t } = useLocale();

  const primaryPost = findPostBySlug(lang, slug);
  const fallbackPost =
    !primaryPost && lang !== "en" ? findPostBySlug("en", slug) : null;
  const post = primaryPost ?? fallbackPost;
  const galleryImages = Array.isArray(post?.gallery) ? post.gallery : [];
  const [activeIndex, setActiveIndex] = useState(null);
  const totalImages = galleryImages.length;

  const closeLightbox = useCallback(() => {
    setActiveIndex(null);
  }, []);

  const showPrevious = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null || totalImages === 0) return current;
      return (current + totalImages - 1) % totalImages;
    });
  }, [totalImages]);

  const showNext = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null || totalImages === 0) return current;
      return (current + 1) % totalImages;
    });
  }, [totalImages]);

  useEffect(() => {
    if (activeIndex === null) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeLightbox();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        showPrevious();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        showNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, closeLightbox, showNext, showPrevious]);

  const openLightbox = useCallback((index) => {
    setActiveIndex(index);
  }, []);

  const currentImage =
    activeIndex !== null && galleryImages[activeIndex]
      ? galleryImages[activeIndex]
      : null;
  const localeCode = lang === "mk" ? "mk-MK" : "en-GB";

  if (!post) {
    return (
      <div>
        <Navbar />
        <div className="home-container">
          <p>{t.newsDetail.notFound}</p>
          <Link className="back-link" to="/news">
            {t.newsDetail.backToNews}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="home-container">
        <Link className="back-link" to="/news">
          {t.newsDetail.backToNews}
        </Link>
        <article className="news-article">
          <header>
            <h1>{post.title}</h1>
            <p className="news-article-meta">
              {new Date(post.date).toLocaleDateString(localeCode, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
              {post.author ? `${t.newsDetail.metaSeparator}${post.author}` : ""}
            </p>
            <img
              className="news-article-hero"
              src={post.mainImage.src}
              alt={post.mainImage.alt}
            />
          </header>

          <section className="news-article-content">
            {post.content.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </section>

          {galleryImages.length > 0 && (
            <section className="news-gallery">
              {galleryImages.map((image, index) => (
                <figure key={image.src}>
                  <button
                    type="button"
                    className="news-gallery-button"
                    onClick={() => openLightbox(index)}
                  >
                    <img src={image.src} alt={image.alt} />
                  </button>
                  {image.alt ? <figcaption>{image.alt}</figcaption> : null}
                </figure>
              ))}
            </section>
          )}
        </article>
      </div>
      {currentImage && (
        <div
          className="lightbox-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={closeLightbox}
        >
          <button
            type="button"
            className="lightbox-close"
            onClick={(event) => {
              event.stopPropagation();
              closeLightbox();
            }}
            aria-label="Close gallery"
          >
            ×
          </button>
          {totalImages > 1 && (
            <button
              type="button"
              className="lightbox-nav lightbox-nav--prev"
              onClick={(event) => {
                event.stopPropagation();
                showPrevious();
              }}
              aria-label="Previous image"
            >
              ‹
            </button>
          )}
          <figure
            className="lightbox-content"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              className="lightbox-image"
              src={currentImage.src}
              alt={currentImage.alt}
            />
            {currentImage.alt ? (
              <figcaption>{currentImage.alt}</figcaption>
            ) : null}
          </figure>
          {totalImages > 1 && (
            <button
              type="button"
              className="lightbox-nav lightbox-nav--next"
              onClick={(event) => {
                event.stopPropagation();
                showNext();
              }}
              aria-label="Next image"
            >
              ›
            </button>
          )}
        </div>
      )}
      <Footer />
    </div>
  );
}

export default NewsDetail;
